/**
 * How a load is doing, in the words of the redesigned "Загрузки" section:
 * one status word per load, one phrase per failure, and where its fix lives.
 *
 * The status word comes from the backend's health summary (pipelines.health:
 * off / paused / failed / late / ok); the reason and the fix are worded here from the
 * pipeline's flags and its latest run. "Отклонение" waits for data quality.
 */
import { whenInline } from 'utils/ru';
import { t } from 'i18n';

const SUPPORT_EMAIL = 'support@evolveanalytics.io';

// PipelineRun.failure_category → the phrase and the screen that fixes it.
export const FAILURE = {
  get auth() { return { text: t('data.sourceDeniedAccess'), fix: 'source' }; },
  get connection() { return { text: t('data.couldNotReachTheSource'), fix: 'source' }; },
  get config() { return { text: t('data.invalidConfiguration'), fix: 'source' }; },
  get upstream() { return { text: t('data.sourceSideFailure'), fix: 'retry' }; },
  get destination() { return { text: t('data.failedToWriteToTheWarehouseOnOurSide'), fix: 'support' }; },
  get unknown() { return { text: t('data.unknownFailure'), fix: 'support' }; },
};

export const failureOf = (run) => FAILURE[run?.failure_category] || FAILURE.unknown;

const TRIGGER = { get scheduled() { return t('data.scheduled'); }, get manual() { return t('data.manual'); }, get backfill() { return t('data.historyBackfill'); } };

/** "по расписанию", "вручную, попытка 2". */
export function triggerText(run) {
  const base = TRIGGER[run?.trigger] || run?.trigger || '';
  return run?.attempt > 1 ? t('data.valueAttemptValue', { value1: base, value2: run.attempt }) : base;
}

/** A support e-mail that already names the pipeline and the run. */
export function supportHref(pipelineName, run) {
  const subject = t('data.pipelineValueRunValue', { value1: pipelineName, value2: run?.name || run?.public_id || '—' });
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

export const STATE = {
  get ok() { return { label: t('data.working'), chip: 'success' }; },
  get late() { return { label: t('data.late'), chip: 'warning' }; },
  get paused() { return { label: t('data.paused'), chip: 'warning' }; },
  get failed() { return { label: t('data.failed2'), chip: 'error' }; },
  get off() { return { label: t('data.disabled2'), chip: 'default' }; },
};

/**
 * The load's status word with its reason and, for a failure, the fix.
 * `health` is the backend summary; without it (still loading) the word is
 * derived from the pipeline alone, which cannot tell "late".
 */
export function loadState(pipeline, health) {
  const last = pipeline?.last_run;
  if (pipeline?.paused_by_billing) {
    return { key: 'off', reason: t('data.subscriptionSuspended') };
  }
  // The source's failed access test pauses the load (and clears is_active):
  // not the tenant's choice, so not "выключена". It resumes once the test passes.
  if (pipeline?.paused_by_connection) {
    return { key: 'paused', reason: t('data.sourceAccessCheckFailedThePipelineIsWaiting'), fix: 'source' };
  }
  if (!pipeline?.is_active) {
    return { key: 'off', reason: t('data.pipelineDisabled2') };
  }
  if (pipeline?.source_connection_status === 'error') {
    return { key: 'failed', reason: t('data.theSourceDeniedAccess2'), fix: 'source' };
  }
  if (last?.status === 'failed') {
    const f = failureOf(last);
    return { key: 'failed', reason: `${f.text}.`, fix: f.fix, run: last };
  }
  if (health?.state === 'late') {
    return {
      key: 'late',
      reason: t('data.expectedValue', { value1: whenInline(health.late_since) }),
    };
  }
  return { key: 'ok' };
}
